export type OAuthProvider = 'apple' | 'google';

const OAUTH_CTX_KEY = '__pm_oauth_ctx__';
const OAUTH_CTX_TTL_MS = 15 * 60 * 1000;

type OAuthContext = {
  provider: OAuthProvider;
  returnTo: string;
  createdAt: number;
};

function safeParse(json: string): unknown {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isSafeReturnTo(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
}

function isExpired(createdAt: number): boolean {
  return Date.now() - createdAt > OAUTH_CTX_TTL_MS;
}

// Persisted immediately before initOAuth() so that if Apple/Google forces a
// full top-level redirect (popups are blocked on most mobile/in-app browsers),
// the app can recover which page to return the user to once AuthInitializer
// picks the session back up after reload — sessionStorage survives the
// redirect round-trip even though all React/router state does not.
export function setOAuthContext(provider: OAuthProvider, returnTo: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeReturnTo(returnTo)) return;

  const ctx: OAuthContext = { provider, returnTo, createdAt: Date.now() };
  try {
    window.sessionStorage.setItem(OAUTH_CTX_KEY, JSON.stringify(ctx));
  } catch {
    // ignore storage failures (private mode / quota)
  }
}

export function getOAuthContext(): OAuthContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(OAUTH_CTX_KEY);
    if (!raw) return null;

    const parsed = safeParse(raw) as Partial<OAuthContext> | null;
    if (!parsed) return null;

    const { provider, returnTo, createdAt } = parsed;
    if (provider !== 'apple' && provider !== 'google') return null;
    if (!isSafeReturnTo(returnTo)) return null;
    if (typeof createdAt !== 'number') return null;
    if (isExpired(createdAt)) return null;

    return { provider, returnTo, createdAt };
  } catch {
    return null;
  }
}

export function clearOAuthContext(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(OAUTH_CTX_KEY);
  } catch {
    // ignore
  }
}
